import { z } from 'zod';

import { CandidateReviewFieldsSchema, PaidAdCopyGenerationRequestSchema } from './copy-assistant';
import { META_CTA_OPTIONS, type MetaCallToAction } from './meta/options';

const UuidSchema = z.string().uuid();
const HashSchema = z.string().regex(/^[a-f0-9]{64}$/i);
const MetaCtaSchema = z.enum(
  META_CTA_OPTIONS.map((option) => option.value) as [MetaCallToAction, ...MetaCallToAction[]],
);

const MetaPreviewSchema = z
  .object({
    primaryText: z.string().trim().max(2_200),
    headline: z.string().trim().max(255),
    description: z.string().trim().max(255),
    cta: MetaCtaSchema.nullable(),
  })
  .strict();

const MetaPatchSchema = z.discriminatedUnion('field', [
  z
    .object({ field: z.literal('primaryText'), primaryText: z.string().trim().min(1).max(2_200) })
    .strict(),
  z.object({ field: z.literal('headline'), headline: z.string().trim().min(1).max(255) }).strict(),
  z.object({ field: z.literal('description'), description: z.string().trim().max(255) }).strict(),
  z
    .object({
      field: z.literal('all'),
      primaryText: z.string().trim().min(1).max(2_200),
      headline: z.string().trim().min(1).max(255),
      description: z.string().trim().max(255),
      cta: MetaCtaSchema,
    })
    .strict(),
]);

const GooglePreviewSchema = z
  .object({
    headlines: z.array(z.string().trim().max(30)).max(15),
    descriptions: z.array(z.string().trim().max(90)).max(4),
  })
  .strict();

const GooglePatchSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('all'),
      headlines: z.array(z.string().trim().min(1).max(30)).min(3).max(15),
      descriptions: z.array(z.string().trim().min(1).max(90)).min(2).max(4),
    })
    .strict(),
  z
    .object({
      kind: z.literal('headline'),
      localAssetId: UuidSchema,
      text: z.string().trim().min(1).max(30),
    })
    .strict(),
  z
    .object({
      kind: z.literal('description'),
      localAssetId: UuidSchema,
      text: z.string().trim().min(1).max(90),
    })
    .strict(),
]);

const GroundingReceiptSchema = z
  .object({
    evidenceIds: z.array(z.string().min(1).max(200)).min(1).max(20),
    /** Exact server-owned evidence receipts actually cited by the generated copy. */
    citationIds: z.array(z.string().min(1).max(200)).min(1).max(20),
    brandSnapshotHash: HashSchema,
    copyContextSnapshotHash: HashSchema,
    summary: z.string().trim().min(1).max(240),
    limitations: z.array(z.string().trim().min(1).max(240)).min(1).max(3),
  })
  .strict();

export const PaidAdCopySuggestionRunPayloadSchema = z.union([
  z
    .object({
      provider: z.literal('meta-ads'),
      requestHash: HashSchema,
      target: PaidAdCopyGenerationRequestSchema.shape.target.and(
        z.object({ provider: z.literal('meta-ads') }).passthrough(),
      ),
      candidates: z
        .array(
          z
            .object({
              localCandidateId: UuidSchema,
              preview: MetaPreviewSchema,
              patch: MetaPatchSchema,
              rationale: z.string().trim().min(1).max(240),
              ...CandidateReviewFieldsSchema,
            })
            .strict(),
        )
        .min(1)
        .max(3),
      grounding: GroundingReceiptSchema,
    })
    .strict()
    .superRefine((payload, context) => {
      payload.candidates.forEach((candidate, index) => {
        if (candidate.patch.field !== payload.target.field) {
          context.addIssue({
            code: 'custom',
            path: ['candidates', index, 'patch'],
            message: 'Meta candidate patch must match the requested field.',
          });
        }
      });
    }),
  z
    .object({
      provider: z.literal('google-ads'),
      requestHash: HashSchema,
      target: PaidAdCopyGenerationRequestSchema.shape.target.and(
        z.object({ provider: z.literal('google-ads') }).passthrough(),
      ),
      candidates: z
        .array(
          z
            .object({
              localCandidateId: UuidSchema,
              preview: GooglePreviewSchema,
              patch: GooglePatchSchema,
              rationale: z.string().trim().min(1).max(240),
              ...CandidateReviewFieldsSchema,
            })
            .strict(),
        )
        .min(1)
        .max(3),
      grounding: GroundingReceiptSchema,
    })
    .strict()
    .superRefine((payload, context) => {
      payload.candidates.forEach((candidate, index) => {
        const field = payload.target.field;
        if (
          candidate.patch.kind !== field.kind ||
          (field.kind !== 'all' &&
            candidate.patch.kind !== 'all' &&
            candidate.patch.localAssetId !== field.localAssetId)
        ) {
          context.addIssue({
            code: 'custom',
            path: ['candidates', index, 'patch'],
            message: 'Google candidate patch must match the requested asset.',
          });
        }
      });
    }),
]);
export type PaidAdCopySuggestionRunPayload = z.infer<typeof PaidAdCopySuggestionRunPayloadSchema>;

export const PaidAdCopySuggestionRunResponseSchema = z.discriminatedUnion('status', [
  z
    .object({
      ok: z.literal(true),
      status: z.literal('ready'),
      suggestionId: UuidSchema,
      draftId: UuidSchema,
      draftRevision: z.number().int().positive(),
      replayed: z.boolean(),
      modelRoute: z.string().min(1).max(240),
      payload: PaidAdCopySuggestionRunPayloadSchema,
    })
    .strict(),
  z
    .object({
      ok: z.literal(true),
      status: z.literal('pending'),
      suggestionId: UuidSchema,
      draftId: UuidSchema,
      draftRevision: z.number().int().positive(),
      replayed: z.literal(true),
    })
    .strict(),
]);
export type PaidAdCopySuggestionRunResponse = z.infer<typeof PaidAdCopySuggestionRunResponseSchema>;

export const PaidAdCopySuggestionRunErrorSchema = z
  .object({
    error: z.string().min(1),
    code: z.string().min(1),
    status: z.number().int().min(400).max(599).optional(),
  })
  .passthrough();
